import { Flex, Modal } from '@mantine/core';

import { usePatchFormAttachment, usePostFormAttachment } from 'api/forms';
import { useShowNotification } from 'hooks';
import type { FormAttachment } from 'types';

import { useTranslation } from 'react-i18next';
import { CheckboxField, Form, FormButton, TextField } from 'components/shared';
import { useFormEditContext } from '../../context';

type Props = {
  opened: boolean;
  onClose: () => void;
  attachment?: FormAttachment;
};

type FormData = Pick<FormAttachment, 'name' | 'description' | 'required'>;

export const AttachmentModal = ({ opened, onClose, attachment }: Props) => {
  const { t } = useTranslation(['forms', 'common']);
  const isEditing = attachment !== undefined;
  const {
    form: { id: formId },
  } = useFormEditContext();
  const defaultValues = {
    name: isEditing ? attachment.name : '',
    description: isEditing ? attachment.description : '',
    required: isEditing ? attachment.required : true,
  };
  const showNotification = useShowNotification();
  const { mutateAsync: postFormAttachment } = usePostFormAttachment({ formId });
  const { mutateAsync: patchFormAttachment } = usePatchFormAttachment({
    formId,
    attachmentId: isEditing ? attachment.id : '',
  });

  const postSubmitHandler = async (data: FormData) => {
    await postFormAttachment({
      ...data,
      description: data.description !== '' ? data.description : null,
    });
    showNotification({
      message: t('editView.sidebar.attachment.created'),
      variant: 'success',
    });
    onClose();
  };

  const patchSubmitHandler = async (data: FormData) => {
    await patchFormAttachment({
      ...data,
      description: data.description !== '' ? data.description : null,
    });
    showNotification({
      message: t('editView.sidebar.attachment.updated'),
      variant: 'success',
    });
    onClose();
  };

  const showNotificationError = () => {
    if (isEditing) {
      showNotification({
        message: t('editView.sidebar.attachment.updatedError'),
        variant: 'error',
      });
    } else {
      showNotification({
        message: t('editView.sidebar.attachment.createdError'),
        variant: 'error',
      });
    }
  };

  const onSubmit = async (data: FormData) => {
    if (isEditing) {
      await patchSubmitHandler(data);
    } else {
      await postSubmitHandler(data);
    }
  };

  const closeHandler = () => {
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={closeHandler}
      title={t('editView.sidebar.attachment.modal.title')}
      size="md"
      centered
    >
      <Form
        onSubmit={onSubmit}
        onError={showNotificationError}
        defaultValues={defaultValues}
      >
        <Flex direction="column">
          <TextField
            name="name"
            rules={{
              required: true,
              maxLength: 100,
            }}
            label={t('editView.sidebar.attachment.modal.name')}
            placeholder={t('editView.sidebar.attachment.modal.namePlaceholder')}
          />
          <TextField
            name="description"
            rules={{
              maxLength: 255,
            }}
            label={t('editView.sidebar.attachment.modal.description')}
            placeholder={t(
              'editView.sidebar.attachment.modal.descriptionPlaceholder',
            )}
          />
          <CheckboxField
            name="required"
            label={t('editView.sidebar.attachment.modal.required')}
          />
        </Flex>
        <Flex
          justify="flex-end"
          gap="md"
          sx={theme => ({ marginTop: theme.other.spacing(3) })}
        >
          <FormButton
            variant="default"
            disabled={state => state.isSubmitting}
            onClick={closeHandler}
          >
            {t('common:cancel')}
          </FormButton>
          <FormButton type="submit" disabled={state => !state.isDirty}>
            {t('common:save')}
          </FormButton>
        </Flex>
      </Form>
    </Modal>
  );
};
